// Tema de dados do CRM — fonte ÚNICA de cor para gráfico, medidor e badge por
// faixa. Antes disto havia 8 paletas independentes espalhadas pelos painéis
// (duas com as MESMAS cores em ordem diferente) e a escala de uso duplicada
// literalmente entre SystemMonitor e SistemaPanel.
//
// A paleta foi VALIDADA contra a superfície do tema neu (#e6e9f0, que é mais
// escura que o branco de antes) com o validador da skill `dataviz`:
//
//   node scripts/validate_palette.js "<hex,...>" --mode light --surface "#e6e9f0"
//
// TEMA ESCURO (2026-08-04): revalidada contra a base escura do neu (#2a2d34) e
// passou em TUDO — inclusive nos dois pontos que o claro só passa com ressalva
// (daltonismo subiu pra ΔE 12.5 e as 8 cores ficaram acima de 3:1 de contraste).
// Ou seja: as MESMAS 8 cores servem os dois temas, não há paleta escura à parte.
//
//   node scripts/validate_palette.js "<hex,...>" --mode dark --surface "#2a2d34"
//
// AXIS e GRID já seguem o tema sozinhos (leem var(--muted-foreground)/var(--border)).
// LEVEL_TEXT e LEVEL_BAR são classes Tailwind e já trazem variante `dark:`.
//
// ATENÇÃO ao ligar LEVEL, SEQ5 ou CURSOR (hoje sem nenhum consumidor): os três
// são hex fixos calibrados para fundo CLARO e reprovam sobre a base escura —
// LEVEL fica entre 2.19:1 e 2.9:1, os dois degraus finais da SEQ5 somem, e o
// CURSOR é uma lavagem ardósia que não existe no escuro. Quem for usá-los
// precisa dar a eles um valor por tema (via var() no CSS) ANTES de plugar.
//
// As cores 500 originais deixavam 5 de 8 abaixo de 3:1 de contraste (âmbar 1.77,
// lima 1.63 — invisíveis em barra fina). Escurecer TUDO um degrau reprova: a
// separação para daltônicos colapsa (rosa×esmeralda ΔE 1.1) e o ciano passa a
// ler como cinza. A solução que passa mantém as 8 famílias, escurece só âmbar,
// lima e ciano, e REORDENA para separar vermelho de âmbar:
//
//   PASS faixa de luz · PASS croma · PASS visão normal (ΔE 21.1)
//   WARN daltônicos (7.9, na banda 6–8) · WARN contraste (3 cores)
//
// Os dois WARN são OBRIGAÇÕES, não observações: exigem codificação secundária.
// Regra do sistema, portanto — legenda sempre presente com 2+ séries, rótulo
// direto até 4 séries, folga de 2px entre marcas vizinhas, e visão de tabela
// disponível. Cor NUNCA pode ser o único canal.
//
// ANTES DE MUDAR QUALQUER COR AQUI, rode o validador de novo.

// ATUALIZACAO 2026-07-25: o slot 1 saiu de #059669 (esmeralda) para #0d9488
// (teal-600), alinhando os tres apps. A troca veio do fin, onde o teal da marca
// (#0e7c6b) reprovou no piso de croma — le como cinza sobre a base neu. O
// teal-600 passa em tudo E sobe o pior par de daltonismo de 7.9 para 12.5,
// entao esta paleta ficou melhor que a original em vez de so diferente.
export const CHART_COLORS = [
  '#0d9488', // 1 teal (marca)
  '#d97706', // 2 âmbar
  '#3b82f6', // 3 azul
  '#ef4444', // 4 vermelho
  '#0891b2', // 5 ciano
  '#8b5cf6', // 6 violeta
  '#65a30d', // 7 lima
  '#ec4899', // 8 rosa
] as const

// Acima de 8 séries a cor deixa de separar: dobre em "Outros", facete em small
// multiples, ou troque para composição. NUNCA gerar um 9º matiz.
export const MAX_SERIES = CHART_COLORS.length

export function seriesColor(i: number): string {
  return CHART_COLORS[i % CHART_COLORS.length]
}

// Cromo do gráfico. Tokens do tema, não hex solto — e com o hsl() que faltava
// em 5 lugares (stroke="var(--border)" é inválido e o recharts caía no #ccc).
export const AXIS = 'hsl(var(--muted-foreground))'
export const GRID = 'hsl(var(--border))'
// cursor de hover: o rgba(255,255,255,0.05) de antes é branco e some sobre a
// base neu.
export const CURSOR = 'rgba(30,41,59,0.06)'

// Rampa sequencial (magnitude). Uma matiz só, do claro ao escuro — nunca arco-íris.
export const SEQ5 = ['#dfe4ee', '#b3c4de', '#7f9cc9', '#4a74b3', '#1d4ed8'] as const

// ── Faixas de estado ────────────────────────────────────────────────────────
// Estas cores são SEMÂNTICAS: a matiz carrega significado e não muda. Só a
// luminosidade foi ajustada para a base neu.
export type Level = 'ok' | 'warn' | 'bad' | 'idle'

export const LEVEL: Record<Level, string> = {
  ok: '#047857',
  warn: '#a16207',
  bad: '#be123c',
  idle: '#64748b',
}

export const LEVEL_TEXT: Record<Level, string> = {
  ok: 'text-emerald-600 dark:text-emerald-400',
  warn: 'text-amber-600 dark:text-amber-400',
  bad: 'text-red-600 dark:text-red-400',
  idle: 'text-muted-foreground',
}

export const LEVEL_BAR: Record<Level, string> = {
  ok: 'bg-emerald-500',
  warn: 'bg-amber-500',
  bad: 'bg-red-500',
  idle: 'bg-muted-foreground/40',
}

/**
 * Classifica um valor em faixa. Substitui os if/else de 3–4 degraus escritos à
 * mão e duplicados entre painéis.
 *
 * `dir` diz de que lado mora o problema: 'higherIsWorse' para uso de disco/CPU
 * (85% é ruim), 'higherIsBetter' para taxa de leitura (45% é ruim).
 */
export function levelOf(
  value: number | null | undefined,
  warnAt: number,
  badAt: number,
  dir: 'higherIsWorse' | 'higherIsBetter' = 'higherIsWorse',
): Level {
  if (value == null || Number.isNaN(value)) return 'idle'
  if (dir === 'higherIsWorse') {
    if (value >= badAt) return 'bad'
    if (value >= warnAt) return 'warn'
    return 'ok'
  }
  if (value <= badAt) return 'bad'
  if (value <= warnAt) return 'warn'
  return 'ok'
}

/** Atalho para a escala de uso de recurso (CPU, RAM, disco): 60 avisa, 85 alerta. */
export const usageLevel = (p: number | null | undefined): Level => levelOf(p, 60, 85)
